import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const Header = ({ onOpenCart, cartCount }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home'); // Estado para la sección activa
  const location = useLocation();
  
  const [theme, setTheme] = useState(() => {
    if (localStorage.getItem('theme')) return localStorage.getItem('theme');
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });

  // --- LÓGICA DE DETECCIÓN DE SCROLL (Intersection Observer) ---
  useEffect(() => {
    // Solo activamos el observador si estamos en la página principal
    if (location.pathname !== '/') return;

    const sectionIds = ['home', 'products', 'gallery', 'about', 'contact'];
    
    const observerOptions = {
      root: null,
      rootMargin: '-20% 0px -70% 0px', // Detecta la sección cuando está cerca de la parte superior
      threshold: 0
    };

    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    sectionIds.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [location.pathname]);

  // --- LÓGICA DE TEMA ---
  useEffect(() => {
    theme === 'dark' ? document.documentElement.classList.add('dark') : document.documentElement.classList.remove('dark');
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => setTheme(theme === 'light' ? 'dark' : 'light');

  // --- FUNCIÓN PARA CLASES ACTIVAS ---
  const getLinkClass = (id) => {
    const base = "transition duration-300 px-3 py-2 rounded-lg font-bold uppercase text-sm tracking-widest block lg:inline-block";
    
    // Si el ID de la sección coincide con la sección activa, iluminamos
    const isCurrent = activeSection === id;

    return isCurrent 
      ? `${base} bg-yellow-700/30 dark:bg-[#594400] text-white dark:text-[#FFE082] shadow-inner` 
      : `${base} text-white/90 dark:text-[#FFD54F]/70 hover:text-yellow-200 hover:bg-yellow-500/20`;
  };

  return (
    <header className="sticky top-0 w-full z-50 bg-yellow-600 dark:bg-[#1C1B17] shadow-xl border-b border-transparent dark:border-[#594400]">
      <nav className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
        
        {/* LOGO */}
        <a href="/" className="flex items-center gap-3">
          <img src="/images/logo_paa_2.png" alt="Logo" className="h-10 sm:h-12 w-auto" />
          <h1 className="text-white dark:text-[#FFD54F] text-lg font-black hidden md:block uppercase tracking-tighter leading-none">
            Productos Apícolas Artesanales
          </h1>
        </a>

        {/* ACCIONES Y MENÚ */}
        <div className="flex items-center gap-4 sm:gap-6">
          
          {/* LINKS ESCRITORIO */}
          <div className="hidden lg:flex gap-2">
            <a href="/#home" className={getLinkClass('home')}>Inicio</a>
            <a href="/#products" className={getLinkClass('products')}>Productos</a>
            <a href="/#gallery" className={getLinkClass('gallery')}>Galería</a>
            <a href="/#about" className={getLinkClass('about')}>Nosotros</a>
            <a href="/#contact" className={getLinkClass('contact')}>Contacto</a>
          </div>

          <button onClick={toggleTheme} className="text-2xl hover:scale-110 transition-transform p-2 rounded-full hover:bg-white/10">
            {theme === 'light' ? '🌙' : '☀️'}
          </button>

          <button onClick={onOpenCart} className="relative group text-white dark:text-[#FFD54F]">
            <svg className="w-8 h-8 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"></path>
            </svg>
            {cartCount > 0 && (
              <span className="absolute -top-2 -right-2 bg-red-600 dark:bg-[#FFD54F] text-white dark:text-[#1C1B17] text-[10px] font-black w-5 h-5 flex items-center justify-center rounded-full animate-bounce">
                {cartCount}
              </span>
            )}
          </button>

          {/* 🍔 BOTÓN HAMBURGUESA (Móvil) */}
          <button 
            className="lg:hidden text-white dark:text-[#FFD54F] p-2"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {isMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16m-7 6h7" strokeWidth="2" />
              )}
            </svg>
          </button>
        </div>
      </nav>

      {/* 📱 MENÚ DESPLEGABLE MÓVIL */}
      {isMenuOpen && (
        <div className="lg:hidden bg-yellow-700 dark:bg-[#2A2821] border-t border-yellow-500/30 dark:border-[#594400] p-4 shadow-inner">
          <div className="flex flex-col gap-2">
            <a href="/#home" className={getLinkClass('home')} onClick={() => setIsMenuOpen(false)}>Inicio</a>
            <a href="/#products" className={getLinkClass('products')} onClick={() => setIsMenuOpen(false)}>Productos</a>
            <a href="/#gallery" className={getLinkClass('gallery')} onClick={() => setIsMenuOpen(false)}>Galería</a>
            <a href="/#about" className={getLinkClass('about')} onClick={() => setIsMenuOpen(false)}>Nosotros</a>
            <a href="/#contact" className={getLinkClass('contact')} onClick={() => setIsMenuOpen(false)}>Contacto</a>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;